import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  BookOpen,
  Calendar,
  Clock,
  ArrowRight,
  Phone,
  Search,
  Tag
} from 'lucide-react';
import { SEO } from '../SEO';
import {
  BLOG_CATEGORIES,
  getCategoryName,
  fetchBlogPosts,
  getBlogPostPath
} from '../blog/blogData';

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

export const BlogPage = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [query, setQuery] = useState('');
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchBlogPosts();
        if (!cancelled) setPosts(data);
      } catch (e) {
        if (!cancelled) setError('Unable to load articles right now.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const filteredPosts = useMemo(() => {
    return posts.filter((post) => {
      const matchCategory =
        activeCategory === 'all' || post.category === activeCategory;
      const q = query.trim().toLowerCase();
      const matchQuery =
        !q ||
        post.title.toLowerCase().includes(q) ||
        post.excerpt.toLowerCase().includes(q) ||
        (post.tags || []).some((t) => t.toLowerCase().includes(q));
      return matchCategory && matchQuery;
    });
  }, [posts, activeCategory, query]);

  const featured = useMemo(
    () => [...posts].sort((a, b) => new Date(b.date) - new Date(a.date))[0],
    [posts]
  );

  const blogListSchema = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'Blog',
      name: 'Swift Move and Clean Blog',
      description:
        'Guides, tips and local insights on moving and cleaning across Indiana from the team at Swift Move and Clean.',
      url: 'https://swiftmoveandclean.com/blog',
      publisher: {
        '@type': 'Organization',
        name: 'Swift Move and Clean',
        logo: {
          '@type': 'ImageObject',
          url: 'https://customer-assets.emergentagent.com/job_swift-movers-1/artifacts/3vyram8e_Gemini_Generated_Image_tphblstphblstphb.png'
        }
      },
      blogPost: posts.map((post) => ({
        '@type': 'BlogPosting',
        headline: post.title,
        description: post.excerpt,
        image: post.cover,
        datePublished: post.date,
        url: `https://swiftmoveandclean.com${getBlogPostPath(post.slug)}`,
        author: { '@type': 'Organization', name: post.author }
      }))
    }),
    [posts]
  );

  return (
    <div className="min-h-screen bg-white pt-20" data-testid="blog-landing-page">
      <SEO
        title="Blog | Moving & Cleaning Tips for Indiana Homes & Businesses"
        description="Expert moving and cleaning tips, checklists, and local Indiana guides from Swift Move and Clean. Learn how to plan a stress-free move, pass move-out inspections, and choose the right cleaning service."
        keywords="moving blog Indiana, cleaning tips Columbus IN, move-out cleaning checklist, moving company blog, commercial relocation guide"
        canonical="/blog"
        schema={[blogListSchema]}
      />

      {/* Hero */}
      <section
        className="text-white py-20"
        style={{
          background:
            'linear-gradient(135deg, #1e3a8a 0%, #2563eb 45%, #0891b2 100%)'
        }}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-3xl">
            <div className="inline-flex items-center space-x-2 bg-white/15 px-4 py-2 rounded-full mb-6 backdrop-blur-sm">
              <BookOpen className="h-4 w-4" />
              <span className="font-medium text-sm">Swift Move and Clean Journal</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Moving & Cleaning Insights from the Heart of Indiana
            </h1>
            <p className="text-lg sm:text-xl text-white/90 leading-relaxed">
              Field-tested checklists, local guides, and behind-the-scenes know-how from the crew
              that has completed over 1,000 moves and cleans across Columbus, Indianapolis, and the
              surrounding communities.
            </p>
          </div>
        </div>
      </section>

      {loading && (
        <div className="container mx-auto px-4 py-20 text-center text-gray-500" data-testid="blog-loading">
          Loading articles...
        </div>
      )}

      {error && !loading && (
        <div className="container mx-auto px-4 py-20 text-center text-red-500" data-testid="blog-error">
          {error}
        </div>
      )}

      {/* Featured Post */}
      {!loading && !error && featured && (
        <section className="container mx-auto px-4 -mt-12 relative z-10">
          <Card
            className="overflow-hidden border-0 shadow-2xl"
            data-testid="blog-featured-post"
          >
            <div className="grid lg:grid-cols-2 gap-0">
              <div
                className="h-64 lg:h-auto bg-cover bg-center"
                style={{ backgroundImage: `url(${featured.cover})` }}
                aria-label={featured.title}
              />
              <CardContent className="p-8 lg:p-12 flex flex-col justify-center">
                <div className="flex items-center gap-3 mb-4">
                  <Badge className="bg-bright-orange hover:bg-bright-orange text-white">
                    Featured
                  </Badge>
                  <Badge variant="outline" className="text-sky-blue border-sky-blue">
                    {getCategoryName(featured.category)}
                  </Badge>
                </div>
                <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-3 leading-tight">
                  {featured.title}
                </h2>
                <p className="text-gray-600 mb-6 leading-relaxed">{featured.excerpt}</p>
                <div className="flex items-center gap-4 text-sm text-gray-500 mb-6">
                  <span className="flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    {formatDate(featured.date)}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    {featured.readTime}
                  </span>
                </div>
                <Link to={getBlogPostPath(featured.slug)}>
                  <Button
                    size="lg"
                    className="bg-navy-blue hover:bg-navy-blue/90 text-white w-full sm:w-auto"
                    data-testid="blog-featured-read-button"
                  >
                    Read the Article
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </Link>
              </CardContent>
            </div>
          </Card>
        </section>
      )}

      {/* Filters + Grid */}
      {!loading && !error && (
        <section className="container mx-auto px-4 py-16">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
            <h2 className="text-3xl font-bold text-gray-900">Latest Articles</h2>

            <div className="relative w-full md:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search articles..."
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-blue focus:border-transparent text-sm"
                data-testid="blog-search-input"
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mb-10" data-testid="blog-category-filters">
            <button
              onClick={() => setActiveCategory('all')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                activeCategory === 'all'
                  ? 'bg-navy-blue text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              data-testid="blog-filter-all"
            >
              All Posts
            </button>
            {BLOG_CATEGORIES.map((cat) => (
              <button
                key={cat.slug}
                onClick={() => setActiveCategory(cat.slug)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  activeCategory === cat.slug
                    ? 'bg-navy-blue text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                data-testid={`blog-filter-${cat.slug}`}
              >
                {cat.name}
              </button>
            ))}
          </div>

          {filteredPosts.length === 0 ? (
            <div className="text-center py-16" data-testid="blog-empty-state">
              <p className="text-gray-500">No articles match your filters. Try clearing the search.</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="blog-posts-grid">
              {filteredPosts.map((post) => (
                <Link
                  key={post.slug}
                  to={getBlogPostPath(post.slug)}
                  className="group"
                  data-testid={`blog-card-${post.slug}`}
                >
                  <Card className="overflow-hidden h-full border-0 shadow-md hover:shadow-xl transition-all duration-300 group-hover:-translate-y-1">
                    <div
                      className="h-48 bg-cover bg-center"
                      style={{ backgroundImage: `url(${post.cover})` }}
                    />
                    <CardContent className="p-6 flex flex-col h-[calc(100%-12rem)]">
                      <Badge
                        variant="outline"
                        className="text-sky-blue border-sky-blue w-fit mb-3"
                      >
                        {getCategoryName(post.category)}
                      </Badge>
                      <h3 className="text-lg font-bold text-gray-900 mb-2 leading-snug group-hover:text-sky-blue transition-colors line-clamp-2">
                        {post.title}
                      </h3>
                      <p className="text-gray-600 text-sm mb-4 flex-grow line-clamp-3">
                        {post.excerpt}
                      </p>
                      <div className="flex items-center justify-between text-xs text-gray-500 border-t pt-3 mt-auto">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {formatDate(post.date)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {post.readTime}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </section>
      )}

      {/* Tag Cloud + CTA */}
      {!loading && !error && posts.length > 0 && (
        <section className="bg-light-gray py-16">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-3 gap-8">
              <Card className="lg:col-span-2 border-0 shadow-md">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Tag className="h-5 w-5 text-sky-blue" />
                    Popular Topics
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {[...new Set(posts.flatMap((p) => p.tags || []))].map((tag) => (
                      <Badge
                        key={tag}
                        variant="secondary"
                        className="bg-white text-gray-700 hover:bg-sky-blue hover:text-white cursor-pointer"
                        onClick={() => setQuery(tag)}
                      >
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-md bg-gradient-to-br from-bright-orange to-orange-600 text-white">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold mb-3">Ready to Move or Refresh?</h3>
                  <p className="text-white/90 mb-6 text-sm leading-relaxed">
                    Skip the planning and let our team handle the heavy lifting. Free quotes, no
                    obligation.
                  </p>
                  <Button
                    className="bg-white text-bright-orange hover:bg-white/90 w-full"
                    onClick={() => (window.location.href = 'tel:8126694165')}
                    data-testid="blog-cta-call-button"
                  >
                    <Phone className="h-4 w-4 mr-2" />
                    (812) 669-4165
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default BlogPage;
